import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  FileText,
  MapPin,
  HelpCircle,
  Inbox,
  Image as ImageIcon,
  Save,
  Trash2,
  Plus,
  Loader2,
  Download,
  ExternalLink,
  Home,
  Users,
  IndianRupee,
  CheckCircle2,
  XCircle,
  Upload,
  BarChart3,
  Search,
  Eye,
  X,
  Filter,
  Mail,
  Phone,
  Lock,
  SlidersHorizontal,
  Check,
} from "lucide-react";
import api, { formatErr, BACKEND_URL } from "../lib/api";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_8f20c47c-6306-4c92-aa61-f6db9e4c740d/artifacts/cc7z0gyj_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2010_37_30%20AM.png";

const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3, group: "App" },
  { key: "listings", label: "Listings", icon: Home, group: "App" },
  { key: "users", label: "Users", icon: Users, group: "App" },
  { key: "unlocks", label: "Unlocks", icon: Lock, group: "App" },
  { key: "payments", label: "Payments", icon: IndianRupee, group: "App" },
  { key: "cities", label: "Cities", icon: MapPin, group: "Shared" },
  { key: "content", label: "Site content", icon: FileText, group: "Web" },
  { key: "media", label: "Images & QR", icon: ImageIcon, group: "Web" },
  { key: "faqs", label: "FAQs", icon: HelpCircle, group: "Web" },
  { key: "enquiries", label: "Leads", icon: Inbox, group: "Web" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const logout = () => {
    localStorage.removeItem("zumroo_token");
    localStorage.removeItem("zumroo_admin_email");
    navigate("/admin/login");
  };

  const email = localStorage.getItem("zumroo_admin_email") || "admin";

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col lg:flex-row">
      <aside className="lg:w-64 lg:min-h-screen bg-white border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col">
        <div className="p-5 flex items-center gap-2 border-b border-slate-200">
          <img src={LOGO_URL} alt="Zumroo" className="w-10 h-10 rounded-lg" />
          <div>
            <div className="font-display font-bold text-lg">
              <span className="text-[#0F172A]">zum</span>
              <span className="text-[#10B981]">roo</span>
            </div>
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Unified CMS</div>
          </div>
        </div>

        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {["App", "Shared", "Web"].map((group) => (
            <div key={group} className="lg:contents">
              <div className="hidden lg:block px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                {group}
              </div>
              {TABS.filter((t) => t.group === group).map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition whitespace-nowrap ${
                      active ? "bg-[#0F172A] text-white" : "text-slate-700 hover:bg-slate-100"
                    }`}
                    data-testid={`admin-tab-${t.key}`}
                  >
                    <Icon className="w-4 h-4" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="mt-auto p-3 border-t border-slate-200 hidden lg:block">
          <div className="text-xs text-slate-500 mb-1.5 px-1">Signed in as</div>
          <div className="text-sm font-semibold text-slate-900 px-1 truncate mb-3">{email}</div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-slate-500 hover:text-slate-900"
          >
            <ExternalLink className="w-4 h-4" /> View site
          </a>
        </div>
        <div className="lg:hidden px-3 pb-3">
          <button onClick={logout} className="text-sm text-slate-500" data-testid="admin-logout-btn-mobile">Sign out</button>
        </div>
      </aside>

      <main className="flex-1 p-5 sm:p-8 lg:p-12 overflow-x-hidden">
        {tab === "overview" && <Overview />}
        {tab === "listings" && <Listings />}
        {tab === "users" && <UsersManager />}
        {tab === "unlocks" && <Unlocks />}
        {tab === "payments" && <Payments />}
        {tab === "cities" && <CityManager />}
        {tab === "content" && <ContentEditor />}
        {tab === "media" && <MediaManager />}
        {tab === "faqs" && <FaqManager />}
        {tab === "enquiries" && <EnquiryManager />}
      </main>
    </div>
  );
}

// ============ OVERVIEW ============
function Overview() {
  const [stats, setStats] = useState(null);
  const [enq, setEnq] = useState(0);
  const [activeProps, setActiveProps] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const [s, e, lst] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/enquiries"),
          api.get("/admin/listings", { params: { status: "approved" } }),
        ]);
        setStats(s.data);
        setEnq(e.data.length);
        const items = Array.isArray(lst.data) ? lst.data : lst.data.items || [];
        setActiveProps(items.length);
      } catch (err) { toast.error(formatErr(err)); }
    })();
  }, []);

  if (!stats) return <Spinner />;
  const cards = [
    { label: "Total Leads", value: enq, icon: Inbox, color: "from-pink-500 to-rose-600", testid: "stat-total-leads" },
    { label: "Active Properties", value: activeProps, icon: Home, color: "from-emerald-500 to-emerald-600", testid: "stat-active-properties" },
    { label: "Total Listings", value: stats.total_listings, icon: BarChart3, color: "from-blue-600 to-blue-700", testid: "stat-total-listings" },
    { label: "Active Users", value: stats.active_users, icon: Users, color: "from-amber-500 to-amber-600", testid: "stat-active-users" },
    { label: "Unlocks", value: stats.total_unlocks, icon: CheckCircle2, color: "from-cyan-500 to-cyan-600", testid: "stat-unlocks" },
    { label: "Revenue", value: stats.revenue_inr, icon: IndianRupee, color: "from-purple-500 to-purple-700", testid: "stat-revenue" },
  ];
  return (
    <div>
      <Header title="Overview" subtitle="Snapshot of mobile-app + website activity." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.testid || c.label} className="zcard p-5 relative overflow-hidden" data-testid={c.testid}>
              <div className={`absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gradient-to-br ${c.color} opacity-10`} />
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center text-white`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="mt-3 text-3xl font-bold tracking-tight">{c.value}</div>
              <div className="text-sm text-slate-500 mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-8 zcard p-5">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Listings by type</div>
        <div className="flex gap-6">
          {Object.entries(stats.listings_by_type || {}).map(([k, v]) => (
            <div key={k}>
              <div className="text-2xl font-bold">{v}</div>
              <div className="text-sm text-slate-500">{k}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ LISTINGS (revenue-gated data table) ============
// Mask helper: keep first `keep` chars, mask the rest with '*'
const maskValue = (val, keep = 4) => {
  if (val === null || val === undefined || val === "") return "—";
  const s = String(val);
  if (s.length <= keep) return s + "*".repeat(Math.max(0, 10 - s.length));
  return s.slice(0, keep) + "*".repeat(s.length - keep);
};

const fmtINR = (n) => {
  const num = Number(n);
  if (!num || isNaN(num)) return "—";
  return "₹" + num.toLocaleString("en-IN");
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return "—"; }
};

function Listings() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  // ---- Column definitions (data-table contract) ----
  // Order matches the canonical 16-column template + admin export contract.
  const COLUMN_DEFS = [
    { key: "date",              label: "Date",              required: false, render: (l) => <span className="text-slate-700">{fmtDate(l.created_at)}</span> },
    { key: "city",              label: "City",              required: false, render: (l) => <span className="text-slate-700">{l.city || "—"}</span> },
    { key: "location",          label: "Location",          required: true,  render: (l) => <span className="text-slate-900 font-medium">{l.locality || l.location || "—"}</span> },
    { key: "society",           label: "Society",           required: false, render: (l) => <span className="text-slate-700">{l.society_name || "—"}</span> },
    { key: "property_type",     label: "Property Type",     required: false, render: (l) => <span className="text-slate-700">{l.property_type || "—"}</span> },
    { key: "configuration",     label: "Configuration",     required: false, render: (l) => <span className="text-slate-700">{l.configuration || (l.bedrooms ? `${l.bedrooms} BHK` : "—")}</span> },
    { key: "carpet_area",       label: "Carpet Area",       required: false, render: (l) => <span className="text-slate-700">{(l.carpet_area || l.area_sqft) ? `${l.carpet_area || l.area_sqft} sqft` : "—"}</span> },
    { key: "floor_details",     label: "Floor Details",     required: false, render: (l) => <span className="text-slate-700">{l.floor_details || "—"}</span> },
    { key: "furnishing_type",   label: "Furnishing Type",   required: false, render: (l) => <span className="text-slate-700">{l.furnishing || "—"}</span> },
    { key: "possession_status", label: "Possession Status", required: false, render: (l) => <span className="text-slate-700">{l.possession_status || "—"}</span> },
    { key: "price",             label: "Price",             required: false, render: (l) => <span className="text-slate-900 font-semibold">{fmtINR(l.price)}</span> },
    { key: "rent_amount",       label: "Rent Amount",       required: false, render: (l) => <span className="text-slate-700">{fmtINR(l.rent_amount)}</span> },
    { key: "deposit_amount",    label: "Deposit Amount",    required: false, render: (l) => <span className="text-slate-700">{fmtINR(l.deposit)}</span> },
    { key: "transaction_type",  label: "Transaction Type",  required: false, render: (l) => <span className="text-slate-700">{l.transaction_type || l.listing_type || "—"}</span> },
    { key: "owner_name",        label: "Owner Name",        required: true,  testid: (l) => `owner-name-${l.id}`,
      render: (l, unlocked) => (
        <>
          <span className={unlocked ? "text-slate-900 font-medium" : "text-slate-500"}>
            {l.contact_name ? (unlocked ? l.contact_name : maskValue(l.contact_name)) : "—"}
          </span>
          {!unlocked && <Lock className="inline w-3 h-3 ml-1.5 text-amber-500" />}
        </>
      ),
    },
    { key: "owner_contact", label: "Owner Contact", required: true, testid: (l) => `mobile-${l.id}`,
      render: (l, unlocked) => {
        const m1 = l.contact_phone || "";
        const m2 = l.contact_phone2 || "";
        const out = [];
        if (m1) out.push(unlocked ? m1 : maskValue(m1));
        if (m2) out.push(unlocked ? m2 : maskValue(m2));
        if (out.length === 0) return <span className="text-slate-400">—</span>;
        // Use the phone value itself as the key — unique within this row,
        // stable across re-renders (won't be reordered).
        return out.map((v) => (
          <div key={v} className={`font-mono text-xs ${unlocked ? "text-slate-900" : "text-slate-500"}`}>{v}</div>
        ));
      },
    },
    { key: "status", label: "Status", required: false, render: (l, unlocked) => (
      <>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
          l.status === "approved" ? "bg-emerald-100 text-emerald-700"
            : l.status === "rejected" ? "bg-red-100 text-red-700"
            : "bg-amber-100 text-amber-700"
        }`}>{l.status}</span>
        <div className={`mt-1 text-[10px] font-bold uppercase tracking-wider ${unlocked ? "text-emerald-600" : "text-slate-400"}`}>
          {unlocked ? "PAID • UNLOCKED" : "UNPAID"}
        </div>
      </>
    )},
  ];

  // ---- Visible columns: persisted in localStorage ----
  const STORAGE_KEY = "zumroo_admin_listing_cols_v2";
  const allKeys = COLUMN_DEFS.map((c) => c.key);
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length) {
          // ensure required keys + filter out unknown keys
          const requiredKeys = COLUMN_DEFS.filter((c) => c.required).map((c) => c.key);
          const merged = Array.from(new Set([...arr.filter((k) => allKeys.includes(k)), ...requiredKeys]));
          return merged;
        }
      }
    } catch (err) {
      console.warn("Failed to restore column preferences from localStorage", err);
    }
    return allKeys;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleCols));
    } catch (err) {
      console.warn("Failed to persist column preferences to localStorage", err);
    }
  }, [visibleCols]);

  const toggleCol = (key) => {
    setVisibleCols((cur) => {
      const def = COLUMN_DEFS.find((c) => c.key === key);
      if (def?.required) return cur; // can't hide required cols
      return cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    });
  };
  const resetCols = () => setVisibleCols(allKeys);
  const visibleColDefs = COLUMN_DEFS.filter((c) => visibleCols.includes(c.key));

  const load = async () => {
    setLoading(true);
    try {
      const params = status === "all" ? {} : { status };
      const { data } = await api.get("/admin/listings", { params });
      setItems(Array.isArray(data) ? data : data.items || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const approve = async (id) => { await api.patch(`/admin/listings/${id}/approve`); toast.success("Approved"); load(); };
  const reject = async (id) => { await api.patch(`/admin/listings/${id}/reject`); toast.success("Rejected"); load(); };
  const toggleUnlock = async (l) => {
    try {
      await api.patch(`/listings/${l.id}`, { unlocked: !l.unlocked });
      toast.success(l.unlocked ? "Locked (data masked)" : "Unlocked (full data revealed)");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const onExcelUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/excel-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Excel imported: ${data.created || 0} created, ${data.updated || 0} updated, ${data.skipped || 0} skipped`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/excel-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "zumroo-listing-template.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const filtered = items.filter((l) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const blob = `${l.locality || ""} ${l.location || ""} ${l.zone || ""} ${l.property_type || ""} ${l.human_id || ""} ${l.contact_name || ""} ${l.contact_phone || ""}`.toLowerCase();
    return blob.includes(q);
  });

  // Close picker when clicking outside
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (!e.target.closest("[data-col-picker]")) setPickerOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  return (
    <div>
      <Header title="Listings" subtitle="Revenue-gated data table. Owner Name & Mobile are masked until a successful payment unlocks the row.">
        <div className="flex flex-wrap gap-2">
          <button onClick={downloadTemplate} className="btn-outline !py-2 !px-4 text-sm" data-testid="excel-template-btn">
            <Download className="w-4 h-4" /> Template
          </button>
          <label className="btn-secondary !py-2 !px-4 text-sm cursor-pointer">
            <Upload className="w-4 h-4" /> Excel upload
            <input type="file" accept=".xlsx" hidden onChange={(e) => onExcelUpload(e.target.files?.[0])} data-testid="excel-upload-input" />
          </label>
        </div>
      </Header>

      <div className="flex flex-col sm:flex-row gap-3 mt-5">
        <div className="flex gap-2 flex-wrap">
          {["all", "pending", "approved", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
                status === s ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-600"
              }`}
              data-testid={`filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="relative flex-1 sm:max-w-xs sm:ml-auto">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search locality, zone, owner…"
            className="w-full pl-10 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-[#10B981]"
            data-testid="listing-search"
          />
        </div>
        {/* Column picker */}
        <div className="relative" data-col-picker>
          <button
            onClick={() => setPickerOpen((o) => !o)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium hover:bg-slate-50"
            data-testid="column-picker-btn"
          >
            <SlidersHorizontal className="w-4 h-4" /> Columns ({visibleCols.length}/{allKeys.length})
          </button>
          {pickerOpen && (
            <div
              className="absolute right-0 top-full mt-2 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-30 p-2 max-h-96 overflow-y-auto"
              data-testid="column-picker-menu"
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 mb-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Show columns</span>
                <button onClick={resetCols} className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 hover:text-emerald-700" data-testid="column-picker-reset">Reset</button>
              </div>
              {COLUMN_DEFS.map((c) => {
                const checked = visibleCols.includes(c.key);
                return (
                  <button
                    key={c.key}
                    onClick={() => toggleCol(c.key)}
                    disabled={c.required}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition ${
                      c.required ? "bg-slate-50 text-slate-400 cursor-not-allowed" : "hover:bg-slate-50 text-slate-700"
                    }`}
                    data-testid={`column-toggle-${c.key}`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${
                      checked ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 bg-white"
                    }`}>
                      {checked && <Check className="w-3 h-3" />}
                    </span>
                    <span className="flex-1">{c.label}</span>
                    {c.required && <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Required</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 zcard !p-0 overflow-hidden" data-testid="listings-table">
        {loading ? (
          <div className="p-8"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No listings.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {visibleColDefs.map((c) => (
                    <th key={c.key} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-600 whitespace-nowrap" data-testid={`col-header-${c.key}`}>{c.label}</th>
                  ))}
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-600 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => {
                  const unlocked = !!l.unlocked;
                  return (
                    <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`listing-row-${l.id}`}>
                      {visibleColDefs.map((c) => (
                        <td
                          key={c.key}
                          className="px-4 py-3 whitespace-nowrap"
                          data-testid={c.testid ? c.testid(l) : `cell-${c.key}-${l.id}`}
                        >
                          {c.render(l, unlocked)}
                        </td>
                      ))}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => approve(l.id)}
                            className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center justify-center"
                            title="Approve"
                            data-testid={`approve-${l.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => reject(l.id)}
                            className="w-8 h-8 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 flex items-center justify-center"
                            title="Reject"
                            data-testid={`reject-${l.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleUnlock(l)}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center ${unlocked ? "bg-amber-50 text-amber-700 hover:bg-amber-100" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                            title={unlocked ? "Lock (mask data)" : "Unlock (reveal data)"}
                            data-testid={`toggle-unlock-${l.id}`}
                          >
                            <Lock className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ USERS ============
function UsersManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | broker | homeowner | inactive_10d
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [campaignBusy, setCampaignBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(Array.isArray(data) ? data : data.items || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleBlock = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { blocked: !u.blocked });
      toast.success(u.blocked ? "User unblocked" : "User blocked");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (filter === "broker" && u.role !== "broker") return false;
      if (filter === "homeowner" && u.role !== "homeowner") return false;
      if (filter === "admin" && u.role !== "admin") return false;
      if (filter === "inactive_10d" && !u.inactive_10d) return false;
      if (filter === "unlockers" && (u.unlocks_count || 0) === 0) return false;
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return `${u.name || ""} ${u.email || ""} ${u.phone || ""}`.toLowerCase().includes(q);
    });
  }, [users, filter, query]);

  const counts = useMemo(() => ({
    all: users.length,
    broker: users.filter((u) => u.role === "broker").length,
    homeowner: users.filter((u) => u.role === "homeowner").length,
    unlockers: users.filter((u) => (u.unlocks_count || 0) > 0).length,
    inactive_10d: users.filter((u) => u.inactive_10d).length,
  }), [users]);

  const toggleOne = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelected((cur) => {
      const allVisibleIds = filtered.map((u) => u.id);
      const allSelected = allVisibleIds.every((id) => cur.has(id));
      const next = new Set(cur);
      if (allSelected) allVisibleIds.forEach((id) => next.delete(id));
      else allVisibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const launchReengage = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const ok = window.confirm(
      `Send a re-engage campaign to ${ids.length} selected user(s)?\n\n` +
      `• Rent unlocks: ₹29 (regular ₹49)\n` +
      `• Sale unlocks: ₹69 (regular ₹79)\n` +
      `• Valid 24 hours\n\n` +
      `Each user gets a unique single-use coupon via SMS + email.`
    );
    if (!ok) return;
    setCampaignBusy(true);
    try {
      const { data } = await api.post("/admin/reengage", {
        user_ids: ids,
        rent_price: 29,
        sale_price: 69,
        hours_valid: 24,
      });
      toast.success(
        `Re-engage sent: ${data.sent_email} emails, ${data.sent_sms} SMS, ${data.issued} coupons issued.`
      );
      setSelected(new Set());
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCampaignBusy(false); }
  };

  const allVisibleChecked = filtered.length > 0 && filtered.every((u) => selected.has(u.id));
  const anySelected = selected.size > 0;

  return (
    <div>
      <Header
        title="Registered Users"
        subtitle={`${counts.all} accounts · ${counts.broker} brokers · ${counts.homeowner} homeowners · ${counts.unlockers} paying users · ${counts.inactive_10d} inactive 10+ days`}
      >
        {anySelected && (
          <button
            onClick={launchReengage}
            disabled={campaignBusy}
            className="btn-primary !py-2 !px-4 text-sm disabled:opacity-50"
            data-testid="reengage-launch-btn"
          >
            {campaignBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Re-engage {selected.size} user{selected.size === 1 ? "" : "s"}
          </button>
        )}
      </Header>

      <div className="flex flex-wrap gap-2 mt-5">
        {[
          { key: "all", label: `All (${counts.all})` },
          { key: "broker", label: `Brokers (${counts.broker})` },
          { key: "homeowner", label: `Owners (${counts.homeowner})` },
          { key: "unlockers", label: `Paying users (${counts.unlockers})` },
          { key: "inactive_10d", label: `Inactive 10+d (${counts.inactive_10d})` },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
              filter === f.key ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-600"
            }`}
            data-testid={`users-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone…"
            className="pl-10 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-[#10B981] w-72"
            data-testid="users-search"
          />
        </div>
      </div>

      <div className="mt-5 zcard !p-0 overflow-hidden" data-testid="users-table">
        {loading ? (
          <div className="p-8"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No users match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleChecked}
                      onChange={toggleAllVisible}
                      className="w-4 h-4 accent-[#1E3F33]"
                      data-testid="users-select-all"
                    />
                  </th>
                  {["User", "Phone", "Email", "Role", "Joined", "Unlocks", "Listings", "Last visit", "Idle days", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-600 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className={`border-b border-slate-100 hover:bg-slate-50 ${selected.has(u.id) ? "bg-emerald-50/50" : ""}`} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(u.id)}
                        onChange={() => toggleOne(u.id)}
                        className="w-4 h-4 accent-[#1E3F33]"
                        data-testid={`user-select-${u.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600 text-xs">
                          {(u.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="text-slate-900 font-medium">{u.name || "(no name)"}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-slate-700">{u.phone || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-700 max-w-[220px] truncate">{u.email || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        u.role === "admin" ? "bg-purple-100 text-purple-700"
                          : u.role === "broker" ? "bg-blue-100 text-blue-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}>{u.role}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-700">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-900 font-semibold text-center">{u.unlocks_count || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-700 text-center">{u.listings_count || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-700">{u.last_visit_at ? fmtDate(u.last_visit_at) : "never"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${
                        u.inactive_10d ? "text-red-600" : "text-emerald-600"
                      }`}>
                        {u.days_since_visit !== null && u.days_since_visit !== undefined ? `${u.days_since_visit}d` : "never"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {u.blocked && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-700">Blocked</span>}
                      {!u.blocked && u.inactive_10d && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Inactive</span>}
                      {!u.blocked && !u.inactive_10d && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {u.role !== "admin" && (
                        <button
                          onClick={() => toggleBlock(u)}
                          className={`px-3 py-1 rounded-lg text-xs font-bold ${
                            u.blocked ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-red-50 text-red-700 hover:bg-red-100"
                          }`}
                          data-testid={`block-${u.id}`}
                        >
                          {u.blocked ? "Unblock" : "Block"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ UNLOCKS / PAYING USERS ============
function Stat({ label, value }) {
  return (
    <div className="zcard !shadow-none border border-slate-200 hover:!shadow-none p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1.5">{value}</div>
    </div>
  );
}

function Unlocks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | revisited | not_revisited | inactive_10d
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/unlocks");
      setItems(Array.isArray(data) ? data : data.items || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((u) => {
    if (filter === "revisited" && !u.revisited_after_unlock) return false;
    if (filter === "not_revisited" && u.revisited_after_unlock) return false;
    if (filter === "inactive_10d" && !u.inactive_10d) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${u.broker_name || ""} ${u.broker_phone || ""} ${u.broker_email || ""} ${u.listing_human_id || ""} ${u.listing_locality || ""} ${u.owner_name || ""}`.toLowerCase().includes(q);
  }), [items, filter, query]);

  const counts = useMemo(() => ({
    all: items.length,
    revisited: items.filter((u) => u.revisited_after_unlock).length,
    not_revisited: items.filter((u) => !u.revisited_after_unlock).length,
    inactive_10d: items.filter((u) => u.inactive_10d).length,
    revenue: items.reduce((s, u) => s + (u.amount || u.amount_inr || 49), 0),
  }), [items]);

  return (
    <div>
      <Header
        title="Unlocks (paying users)"
        subtitle={`${counts.all} successful unlocks · ${counts.revisited} revisited the site after unlock · ${counts.not_revisited} did not return · ${counts.inactive_10d} inactive 10+ days`}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        <Stat label="Total unlocks" value={counts.all} />
        <Stat label="Revenue (₹)" value={counts.revenue.toLocaleString("en-IN")} />
        <Stat label="Revisited" value={counts.revisited} />
        <Stat label="Inactive 10+d" value={counts.inactive_10d} />
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        {[
          { key: "all", label: `All (${counts.all})` },
          { key: "revisited", label: `Revisited (${counts.revisited})` },
          { key: "not_revisited", label: `Not revisited (${counts.not_revisited})` },
          { key: "inactive_10d", label: `Inactive 10+d (${counts.inactive_10d})` },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
              filter === f.key ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-600"
            }`}
            data-testid={`unlocks-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search broker, listing, owner…"
            className="pl-10 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-[#10B981] w-72"
            data-testid="unlocks-search"
          />
        </div>
      </div>

      <div className="mt-5 zcard !p-0 overflow-hidden" data-testid="unlocks-table">
        {loading ? (
          <div className="p-8"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No unlocks match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {["Date", "Buyer", "Buyer phone", "Buyer email", "Listing", "Locality", "Owner unlocked", "Amount", "Last visit", "Revisited?"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-600 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id || `${u.broker_id}-${u.listing_id}-${u.created_at}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`unlock-row-${u.id || u.broker_id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-700">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-900 font-medium">{u.broker_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-slate-700">{u.broker_phone}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-700 max-w-[200px] truncate">{u.broker_email}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-xs font-bold tracking-wider bg-slate-100 text-slate-700 px-2 py-0.5 rounded inline-block">
                        {u.listing_human_id || u.listing_id?.slice(0, 8)}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{u.listing_property_type}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-700">{u.listing_locality} · {u.listing_society}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-slate-900 font-medium">{u.owner_name}</div>
                      <div className="font-mono text-xs text-slate-600">{u.owner_phone}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-slate-900">{fmtINR(u.amount || 49)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-700">{u.last_visit_at ? fmtDate(u.last_visit_at) : "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {u.revisited_after_unlock ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Returned</span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">No revisit</span>
                      )}
                      {u.inactive_10d && <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-red-600">Idle 10+d</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ PAYMENTS ============
function Payments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/payments");
        setItems(Array.isArray(data) ? data : data.items || []);
      } catch (e) { toast.error(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <div>
      <Header title="Payments" subtitle="Razorpay unlock transactions" />
      <div className="mt-6 space-y-3">
        {loading && <Spinner />}
        {!loading && items.length === 0 && <div className="zcard p-8 text-center text-slate-500">No payments yet.</div>}
        {items.map((p) => (
          <div key={p.id || p.order_id} className="zcard !shadow-none border border-slate-200 hover:!shadow-none p-4 flex flex-wrap items-center gap-3" data-testid={`payment-${p.id || p.order_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900">₹{((p.amount || 0) / 100).toLocaleString("en-IN")}</div>
              <div className="text-xs text-slate-500">{p.order_id || p.id}</div>
            </div>
            <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
              p.status === "paid" || p.status === "verified" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
            }`}>{p.status || "pending"}</span>
            <div className="text-xs text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleString() : ""}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ CITIES (new schema is_active/is_default) ============
function CityManager() {
  const [cities, setCities] = useState([]);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);

  const load = async () => {
    const { data } = await api.get("/cities", { params: { active_only: false } });
    setCities(data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return toast.error("Enter a city name");
    try {
      await api.post("/cities", { name: name.trim(), is_active: isActive, is_default: false });
      setName(""); setIsActive(true);
      load();
      toast.success("City added");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const toggleActive = async (c) => {
    await api.patch(`/cities/${c.id}`, { is_active: !c.is_active });
    load();
  };
  const setDefault = async (c) => {
    await api.patch(`/cities/${c.id}`, { is_default: true });
    load();
    toast.success(`${c.name} set as default`);
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    await api.delete(`/cities/${c.id}`);
    load();
  };

  return (
    <div>
      <Header title="Cities" subtitle="Add cities and toggle Live / Coming Soon. The landing page shows inactive cities as 'Coming Soon'." />
      <div className="zcard p-5 mt-6 flex flex-col sm:flex-row gap-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="City name (e.g., Bengaluru)"
          className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#10B981]"
          data-testid="city-name-input"
        />
        <select
          value={isActive ? "live" : "soon"}
          onChange={(e) => setIsActive(e.target.value === "live")}
          className="px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#10B981]"
          data-testid="city-status-select"
        >
          <option value="live">Live</option>
          <option value="soon">Coming Soon</option>
        </select>
        <button onClick={add} className="btn-primary" data-testid="city-add-btn">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      <div className="mt-5 space-y-3">
        {cities.map((c) => (
          <div key={c.id} className="zcard !shadow-none border border-slate-200 hover:!shadow-none p-4 flex items-center gap-3" data-testid={`city-row-${c.name.toLowerCase()}`}>
            <MapPin className={`w-5 h-5 ${c.is_active ? "text-emerald-500" : "text-slate-400"}`} />
            <span className="font-semibold flex-1">{c.name}</span>
            {c.is_default && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">Default</span>
            )}
            <button
              onClick={() => toggleActive(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                c.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
              }`}
              data-testid={`city-toggle-${c.id}`}
            >
              {c.is_active ? "Live" : "Coming Soon"}
            </button>
            {!c.is_default && (
              <button
                onClick={() => setDefault(c)}
                className="px-2.5 py-1.5 rounded-lg text-xs text-slate-600 hover:bg-slate-100"
                data-testid={`city-default-${c.id}`}
              >
                Set default
              </button>
            )}
            <button onClick={() => remove(c)} className="w-9 h-9 rounded-lg hover:bg-red-50 text-red-500 flex items-center justify-center" data-testid={`city-delete-${c.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ CONTENT (landing copy) ============
function ContentEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch((e) => toast.error(formatErr(e)));
  }, []);
  const update = (k) => (e) => setContent({ ...content, [k]: e.target.value });
  const save = async () => {
    try {
      setSaving(true);
      await api.put("/admin/content", content);
      toast.success("Content updated");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };
  if (!content) return <Spinner />;
  const fields = [
    { k: "headline", label: "Headline", type: "text" },
    { k: "subtext", label: "Subtext", type: "textarea" },
    { k: "cta_primary", label: "Primary CTA label", type: "text" },
    { k: "cta_secondary", label: "Secondary CTA label", type: "text" },
    { k: "price_basic", label: "Basic price", type: "text" },
    { k: "price_premium", label: "Premium price", type: "text" },
    { k: "play_store_url", label: "Play Store URL", type: "text" },
    { k: "trust_text", label: "Trust section text", type: "textarea" },
    { k: "contact_phone", label: "Contact phone", type: "text" },
    { k: "contact_email", label: "Contact email", type: "text" },
  ];
  return (
    <div>
      <Header title="Site content" subtitle="Edit hero copy, pricing, CTAs and contact details for the public website." />
      <div className="zcard p-6 sm:p-8 mt-6 space-y-5 max-w-3xl">
        {fields.map((f) => (
          <div key={f.k}>
            <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea
                value={content[f.k] || ""}
                onChange={update(f.k)}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#10B981] focus:ring-2 focus:ring-emerald-100 outline-none"
                data-testid={`content-${f.k}`}
              />
            ) : (
              <input
                value={content[f.k] || ""}
                onChange={update(f.k)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#10B981] focus:ring-2 focus:ring-emerald-100 outline-none"
                data-testid={`content-${f.k}`}
              />
            )}
          </div>
        ))}
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="content-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>
    </div>
  );
}

// ============ MEDIA ============
function MediaManager() {
  const [content, setContent] = useState(null);
  const [uploading, setUploading] = useState("");
  useEffect(() => { api.get("/content").then((r) => setContent(r.data)); }, []);
  const slots = [
    { k: "qr_image_url", label: "QR Code image" },
    { k: "hero_image_url", label: "Hero image" },
    { k: "trust_image_url", label: "Trust / community image" },
    { k: "city_image_url", label: "City skyline image" },
    { k: "residential_image_url", label: "Residential image" },
  ];
  const onUpload = async (slot, file) => {
    if (!file) return;
    try {
      setUploading(slot);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = { ...content, [slot]: data.url };
      await api.put("/admin/content", updated);
      setContent(updated);
      toast.success("Uploaded");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(""); }
  };
  const updateUrl = (k) => (e) => setContent({ ...content, [k]: e.target.value });
  const saveUrls = async () => {
    try { await api.put("/admin/content", content); toast.success("URLs saved"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!content) return <Spinner />;
  const resolveUrl = (u) => (u && !u.startsWith("http") ? `${BACKEND_URL}${u}` : u);
  return (
    <div>
      <Header title="Images & QR" subtitle="Upload a QR code or replace section images. Or paste any direct image URL." />
      <div className="grid sm:grid-cols-2 gap-5 mt-6">
        {slots.map((s) => (
          <div key={s.k} className="zcard p-5">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-700 mb-3">{s.label}</div>
            <div className="aspect-video bg-slate-100 rounded-xl overflow-hidden mb-3 flex items-center justify-center">
              {content[s.k] ? <img src={resolveUrl(content[s.k])} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-8 h-8 text-slate-400" />}
            </div>
            <input
              type="text"
              value={content[s.k] || ""}
              onChange={updateUrl(s.k)}
              placeholder="https://..."
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm mb-2 outline-none focus:border-[#10B981]"
              data-testid={`media-url-${s.k}`}
            />
            <label className="btn-outline !py-2 !px-4 cursor-pointer text-sm w-full justify-center">
              {uploading === s.k ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload file"}
              <input type="file" accept="image/*" className="hidden" data-testid={`media-upload-${s.k}`} onChange={(e) => onUpload(s.k, e.target.files?.[0])} />
            </label>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <button onClick={saveUrls} className="btn-primary" data-testid="media-save-urls-btn">
          <Save className="w-4 h-4" /> Save image URLs
        </button>
      </div>
    </div>
  );
}

// ============ FAQs ============
function FaqManager() {
  const [faqs, setFaqs] = useState([]);
  const [q, setQ] = useState(""); const [a, setA] = useState("");
  const load = () => api.get("/faqs").then((r) => setFaqs(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!q.trim() || !a.trim()) return toast.error("Question and answer are required");
    await api.post("/admin/faqs", { question: q.trim(), answer: a.trim(), order: faqs.length + 1 });
    setQ(""); setA(""); load();
    toast.success("FAQ added");
  };
  const remove = async (f) => {
    if (!window.confirm("Delete this FAQ?")) return;
    await api.delete(`/admin/faqs/${f.id}`);
    load();
  };
  return (
    <div>
      <Header title="FAQs" subtitle="Manage the questions shown on the website." />
      <div className="zcard p-5 mt-6 space-y-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Question" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#10B981]" data-testid="faq-question-input" />
        <textarea value={a} onChange={(e) => setA(e.target.value)} placeholder="Answer" rows={3} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#10B981]" data-testid="faq-answer-input" />
        <button onClick={add} className="btn-primary" data-testid="faq-add-btn">
          <Plus className="w-4 h-4" /> Add FAQ
        </button>
      </div>
      <div className="mt-5 space-y-3">
        {faqs.map((f) => (
          <div key={f.id} className="zcard !shadow-none border border-slate-200 hover:!shadow-none p-5" data-testid={`faq-row-${f.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="font-semibold text-slate-900">{f.question}</div>
                <div className="text-sm text-slate-600 mt-1">{f.answer}</div>
              </div>
              <button onClick={() => remove(f)} className="w-9 h-9 rounded-lg hover:bg-red-50 text-red-500 flex items-center justify-center" data-testid={`faq-delete-${f.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ ENQUIRIES (data table with search + filter + view + delete) ============
function EnquiryManager() {
  const [enq, setEnq] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dateRange, setDateRange] = useState("all"); // all | today | week | month
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/enquiries"); setEnq(data); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/enquiries/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "zumroo_enquiries.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setViewing(null);
      load();
      toast.success("Enquiry deleted");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const filtered = enq.filter((e) => {
    // text search
    const q = query.trim().toLowerCase();
    if (q) {
      const blob = `${e.name || ""} ${e.mobile || ""} ${e.email || ""} ${e.message || ""}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    // date filter
    if (dateRange !== "all") {
      const created = new Date(e.created_at).getTime();
      const now = Date.now();
      const day = 24 * 60 * 60 * 1000;
      const cutoff = dateRange === "today" ? now - day : dateRange === "week" ? now - 7 * day : now - 30 * day;
      if (created < cutoff) return false;
    }
    return true;
  });

  return (
    <div>
      <Header title="Leads" subtitle={`${enq.length} total · ${filtered.length} shown`}>
        <button onClick={exportCsv} className="btn-secondary !py-2.5 !px-5 text-sm" data-testid="enquiry-export-btn">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </Header>

      {/* Search + Filter */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, mobile, email or message…"
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#10B981] focus:ring-2 focus:ring-emerald-100 outline-none bg-white"
            data-testid="enquiry-search-input"
          />
        </div>
        <div className="relative">
          <Filter className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="pl-11 pr-8 py-3 rounded-xl border border-slate-200 focus:border-[#10B981] outline-none bg-white font-medium appearance-none"
            data-testid="enquiry-filter-select"
          >
            <option value="all">All time</option>
            <option value="today">Last 24h</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 zcard !p-0 overflow-hidden" data-testid="enquiries-table">
        {loading ? (
          <div className="p-8"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500 font-medium">{enq.length === 0 ? "No leads yet." : "No leads match your filter."}</div>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Name</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Mobile</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Email</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Message</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Submitted</th>
                    <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-600 whitespace-nowrap w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`enquiry-row-${e.id}`}>
                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-900">{e.name}</div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-700">{e.mobile}</td>
                      <td className="px-5 py-4 text-sm text-slate-500">{e.email || "—"}</td>
                      <td className="px-5 py-4 text-sm text-slate-600 max-w-xs truncate">{e.message}</td>
                      <td className="px-5 py-4 text-xs text-slate-500 whitespace-nowrap">
                        {new Date(e.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        <div className="text-[10px] text-slate-400">
                          {new Date(e.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setViewing(e)}
                            className="w-9 h-9 rounded-lg hover:bg-emerald-50 text-emerald-600 flex items-center justify-center transition"
                            title="View"
                            data-testid={`enquiry-view-${e.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => remove(e.id)}
                            className="w-9 h-9 rounded-lg hover:bg-red-50 text-red-500 flex items-center justify-center transition"
                            title="Delete"
                            data-testid={`enquiry-delete-${e.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile card-rows */}
            <div className="md:hidden divide-y divide-slate-100">
              {filtered.map((e) => (
                <div key={e.id} className="p-4 active:bg-slate-50" data-testid={`enquiry-mobile-${e.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900">{e.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {e.mobile}
                      </div>
                      {e.email && (
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {e.email}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-[10px] text-slate-400">{new Date(e.created_at).toLocaleDateString()}</span>
                      <div className="flex gap-1 mt-1">
                        <button onClick={() => setViewing(e)} className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(e.id)} className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-slate-700 line-clamp-2">{e.message}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* View Modal */}
      {viewing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setViewing(null)}
          data-testid="enquiry-modal"
        >
          <div onClick={(ev) => ev.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-600 mb-1">Lead detail</div>
                <h3 className="text-2xl font-bold tracking-tight">{viewing.name}</h3>
              </div>
              <button onClick={() => setViewing(null)} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid="enquiry-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-5 space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                <Phone className="w-4 h-4 text-emerald-600" />
                <a href={`tel:${viewing.mobile}`} className="font-medium text-slate-900 hover:text-emerald-600">{viewing.mobile}</a>
              </div>
              {viewing.email && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                  <Mail className="w-4 h-4 text-emerald-600" />
                  <a href={`mailto:${viewing.email}`} className="font-medium text-slate-900 hover:text-emerald-600 truncate">{viewing.email}</a>
                </div>
              )}
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Message</div>
                <div className="p-4 rounded-xl bg-slate-50 text-sm text-slate-700 whitespace-pre-line leading-relaxed">{viewing.message}</div>
              </div>
              <div className="text-xs text-slate-400">
                Submitted on {new Date(viewing.created_at).toLocaleString()}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <a
                href={`tel:${viewing.mobile}`}
                className="btn-primary flex-1 justify-center !py-3"
                data-testid="enquiry-modal-call"
              >
                <Phone className="w-4 h-4" /> Call
              </a>
              <button
                onClick={() => remove(viewing.id)}
                className="px-5 py-3 rounded-full bg-red-50 text-red-600 font-semibold hover:bg-red-100 transition flex items-center gap-2"
                data-testid="enquiry-modal-delete"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ helpers ============
function Header({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-slate-500 text-sm mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}
function Spinner() {
  return (
    <div className="flex items-center gap-2 text-slate-500 mt-6">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
}
